import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Heading, Text } from "../../../../components/common/typography";
import { Chip } from "../../../../components/common/chip";
import { SectionCard } from "../../../../components/common/section-card";
import { EmptyState } from "../../../../components/common/empty-state";
import { IconGift } from "@tabler/icons-react";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { AdminLoyaltyRedemptionDto } from "../../hooks/useAdminLoyaltyRedemptions";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

function formatDate(iso?: string | null) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DATE_FMT.format(d);
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Text variant="bodyXs" tone="muted">{label}</Text>
      <Text variant="body" tone="dark">{value}</Text>
    </div>
  );
}

export function AdminLoyaltyRedemptionDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const rd = location.state?.redemption as AdminLoyaltyRedemptionDto | undefined;

  if (!rd) {
    return (
      <div className="flex min-h-full w-full flex-col bg-bgLight px-8 py-8">
        <EmptyState
          icon={IconGift}
          title="Redemption not found"
          description="Navigate here from the Loyalty Points page."
          primaryAction={{
            label: "Back to Loyalty Points",
            onClick: () => navigate(AppRoutes.ADMIN_LOYALTY_CONFIG),
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-full w-full flex-col gap-6 bg-bgLight px-8 py-8">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(AppRoutes.ADMIN_LOYALTY_CONFIG)}
          className="hover:underline"
        >
          <Text variant="bodyBold" tone="accent">← Loyalty Points</Text>
        </button>
        <Text variant="body" tone="muted">/</Text>
        <Text variant="body" tone="muted">Redemption {id}</Text>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Heading variant="h2" tone="dark">Redemption {id}</Heading>
          {rd.isReversed ? (
            <Chip variant="rejected">Reversed</Chip>
          ) : (
            <Chip variant="approved">Active</Chip>
          )}
        </div>
        <Text variant="bodySm" tone="muted">{formatDate(rd.createdAt)}</Text>
      </div>

      <SectionCard title="Redemption details">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Field label="Redemption ID" value={rd.id} />
          <Field label="Points redeemed" value={rd.points.toLocaleString()} />
          <Field label="Reason" value={rd.reason ?? "—"} />
          <Field label="Status" value={rd.isReversed ? "Reversed" : "Active"} />
          <Field label="Reversed at" value={formatDate(rd.reversedAt)} />
          <Field label="Created at" value={formatDate(rd.createdAt)} />
          {rd.saleOrderId != null && (
            <div className="flex flex-col gap-1">
              <Text variant="bodyXs" tone="muted">Linked order</Text>
              <Link
                to={`/admin/sales/${rd.saleOrderId}`}
                className="text-accentGreenDark underline-offset-2 hover:underline"
              >
                <Text variant="body" tone="inherit">#{rd.saleOrderId}</Text>
              </Link>
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard title="User details">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
          <Field label="User ID" value={rd.user.id} />
          <Field label="Company name" value={rd.user.companyName ?? "—"} />
          <Field label="Full name" value={rd.user.name} />
          <Field label="Email" value={rd.user.email ?? "—"} />
          <Field label="Phone" value={rd.user.phoneNumber ?? "—"} />
        </div>
      </SectionCard>
    </div>
  );
}
